import React, { FC, useMemo } from 'react';

import { MaybeTooltip } from '@modernloop/shared/components';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { merge } from 'lodash';

import Chart, { DataPoints, ZeroStateProps, ZeroStateWrapper } from './index';

const DEFAULT_COLORS: string[] = [];

export type Customlabel = {
  key: string;
  label: string;
  percentage: number;
  value: number;
  color: string;
};

export type DataKey = {
  key: string;
  label: string;
};

const defaultApexOptions: ApexOptions = {
  noData: {
    text: 'No data',
    align: 'center',
    verticalAlign: 'middle',
  },
  dataLabels: {
    enabled: false,
  },
  plotOptions: {
    pie: {
      expandOnClick: false,
    },
  },
  stroke: {
    width: 0,
  },
  legend: {
    fontSize: '16px',
    fontFamily: 'Inter',
  },
  tooltip: {
    fillSeriesColor: false,
  },
};

export interface Props {
  series: DataPoints;
  dataKeys: DataKey[];
  zeroState: Omit<ZeroStateProps, 'series' | 'type'>;
  options?: Omit<ApexOptions, 'series'>;
  width?: number;
  height?: number;
  showCustomLabels?: boolean;
}

const PieChart: FC<Props> = ({ options, series, dataKeys, width, height, zeroState, showCustomLabels }) => {
  const theme = useTheme();

  const colors =
    options?.colors ||
    DEFAULT_COLORS.concat([
      theme.palette.background.info,
      theme.palette.action.focus,
      theme.palette.primary.light,
      theme.palette.primary.main,
      theme.palette.primary.dark,
      theme.palette.tertiary.main,
      theme.palette.tertiary.dark,
    ]);

  const computedSeries = useMemo(() => {
    return dataKeys.map((d) => {
      return (
        series &&
        series.reduce((partialSum, a) => {
          if (a.x === d.key && a.y) {
            return partialSum + a.y;
          }
          return partialSum;
        }, 0)
      );
    });
  }, [dataKeys, series]);

  const customlabels: Customlabel[] = useMemo(() => {
    if (!showCustomLabels) {
      return [];
    }
    const total = computedSeries.reduce((partialSum, val) => {
      if (val) {
        return partialSum + val;
      }
      return partialSum;
    }, 0);
    return dataKeys.map((d, index) => {
      const val = computedSeries[index] || 0;
      return {
        key: d.key,
        label: d.label,
        percentage: total ? (val / total) * 100 : 0,
        value: val,
        color: colors[index],
      };
    });
  }, [colors, computedSeries, dataKeys, showCustomLabels]);

  const labels = useMemo(() => {
    return dataKeys.map((d) => {
      return d.label;
    });
  }, [dataKeys]);

  if (!dataKeys.length) {
    return null;
  }

  const apexOptions: ApexOptions = merge(
    { ...defaultApexOptions },
    {
      colors,
      labels,
      legend: {
        show: !showCustomLabels,
      },
    },
    options
  );

  if (showCustomLabels) {
    return (
      <ZeroStateWrapper series={computedSeries} {...zeroState} type="pie">
        <Stack direction="row">
          <Box width="45%">
            <Chart
              options={apexOptions}
              series={computedSeries}
              width={width}
              height={height}
              type={options?.chart?.type || 'pie'}
            />
          </Box>
          <Box mt={2} width="55%">
            <Stack direction="column" spacing={1}>
              {customlabels.map((customLabel) => (
                <Stack sx={{ wrap: 'nowrap' }} spacing={1} key={customLabel.key} direction="row">
                  <Stack alignItems="center" spacing={1} sx={{ wrap: 'nowrap', width: '60%' }} direction="row">
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: customLabel.color,
                      }}
                    />
                    <MaybeTooltip label={customLabel.label || ''} title={customLabel.label || ''} />
                  </Stack>
                  <Typography color="text.secondary">{customLabel.percentage.toFixed(1)}%</Typography>
                  <Typography sx={{ width: '20%' }} color="text.secondary">
                    {customLabel.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </ZeroStateWrapper>
    );
  }

  return (
    <ZeroStateWrapper series={computedSeries} {...zeroState} type="pie">
      <Stack direction="row">
        <Chart
          options={apexOptions}
          series={computedSeries}
          width={width}
          height={height}
          type={options?.chart?.type || 'pie'}
        />
      </Stack>
    </ZeroStateWrapper>
  );
};

export default PieChart;
